import { useCallback, useEffect, useMemo, useState } from 'react'
import { chargerDossiersPme, chargerPlansPme, grilleMensuelle, type DossierPme } from '../../api/onboarding'
import { nomPartie, nomSignataire, type StatutJuridique } from '../../api/designationPartie'
import { LIBELLE_STATUT, messageErreur, statutDe, STATUTS, type PlanPme, type StatutOnboarding } from '../../api/onboardingRegles'
import { useDroits } from '../Contexte'
import { dateCourte } from '../Format'
import { Icone } from '../Icone'
import { BadgeOnboarding } from '../onboarding/Briques'
import { DossierPme as PanneauDossier } from '../onboarding/DossierPme'
import { ModelesContrat } from '../onboarding/ModelesContrat'
import '../onboarding/onboarding.css'
import { ModaleClient } from './Clients'

const LIBELLE_COURT_STATUT: Record<StatutJuridique, string> = { societe: 'Société', entreprenant: 'Entreprenant', personne_physique: 'Personne physique' }

/**
 * Onboarding PME — le pipeline (prospect → pièces → contrat envoyé → contrat signé → actif, plus refusés et
 * suspendus) et, au clic, le dossier du client. Propriétaire et dispatch instruisent ; le comptable lit.
 * Une PME n'a le droit de recevoir des courses qu'une fois ACTIVE : la base le refuse sinon.
 */
export function EcranOnboardingPme({
  orgId,
  clientInitial = null,
  surNotifier,
  surRafraichir,
}: {
  orgId: string
  /** Client à ouvrir d'emblée (entrée « Démarrer l'onboarding » de l'écran Clients). */
  clientInitial?: string | null
  surNotifier: (message: string) => void
  /** Rafraîchit les données générales de la console (liste des clients). */
  surRafraichir: () => Promise<void>
}) {
  const droits = useDroits()
  const [dossiers, setDossiers] = useState<DossierPme[] | null>(null)
  const [plans, setPlans] = useState<PlanPme[]>([])
  const [grille, setGrille] = useState<{ option_longue_distance_mensuel_fcfa: number } | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [filtre, setFiltre] = useState<StatutOnboarding | 'tous'>('tous')
  const [recherche, setRecherche] = useState('')
  const [ouvertId, setOuvertId] = useState<string | null>(clientInitial)
  const [modeles, setModeles] = useState(false)
  const [creation, setCreation] = useState(false)

  const peutGerer = droits.peut('onboarding_gerer') && !droits.apercu
  const proprietaire = droits.peut('onboarding_proprietaire') && !droits.apercu

  const charger = useCallback(async () => {
    if (droits.apercu) { setDossiers([]); return }
    try {
      const [d, p, g] = await Promise.all([chargerDossiersPme(orgId), chargerPlansPme(), grilleMensuelle(orgId)])
      setDossiers(d)
      setPlans(p)
      setGrille(g)
      setErreur(null)
    } catch (e) {
      setErreur(messageErreur(e))
      setDossiers((x) => x ?? [])
    }
  }, [orgId, droits.apercu])

  useEffect(() => { void charger() }, [charger])
  useEffect(() => { if (clientInitial) setOuvertId(clientInitial) }, [clientInitial])

  /** Après une écriture : recharge la liste d'ici ET les données générales (le badge de « Clients »). */
  const apresMaj = useCallback(async () => {
    await Promise.all([charger(), surRafraichir()])
  }, [charger, surRafraichir])

  const compte = useMemo(() => {
    const c = Object.fromEntries(STATUTS.map((s) => [s, 0])) as Record<StatutOnboarding, number>
    for (const d of dossiers ?? []) c[d.onboarding_status] += 1
    return c
  }, [dossiers])

  const visibles = useMemo(() => {
    const q = recherche.trim().toLowerCase()
    return (dossiers ?? []).filter(
      (d) =>
        (filtre === 'tous' || d.onboarding_status === filtre) &&
        (!q || `${d.name} ${d.contact_name ?? ''} ${d.phone ?? ''} ${nomSignataire(d)} ${nomPartie(d)}`.toLowerCase().includes(q)),
    )
  }, [dossiers, filtre, recherche])

  const ouvert = ouvertId ? (dossiers ?? []).find((d) => d.id === ouvertId) ?? null : null

  if (ouvertId && dossiers && !ouvert) {
    return (
      <div className="card" data-anim>
        <div className="card-body">
          <p className="onb-note">Ce dossier est introuvable (client supprimé, ou n’est pas une PME).</p>
          <button className="btn btn-outline btn-sm" onClick={() => setOuvertId(null)}>Retour au pipeline</button>
        </div>
      </div>
    )
  }

  if (ouvert) {
    return (
      <PanneauDossier
        key={ouvert.id}
        orgId={orgId}
        dossier={ouvert}
        plans={plans}
        grille={grille}
        peutGerer={peutGerer}
        proprietaire={proprietaire}
        surRetour={() => setOuvertId(null)}
        surMaj={apresMaj}
      />
    )
  }

  return (
    <>
      {droits.apercu && (
        <div className="bandeau alerte" role="status">L’aperçu de développement n’a pas de base : l’onboarding se consulte avec une connexion réelle.</div>
      )}
      {!peutGerer && !droits.apercu && (
        <div className="bandeau" role="status">Votre rôle consulte l’onboarding sans le modifier.</div>
      )}
      <Erreur texte={erreur} />

      <div className="onb-pipeline" role="tablist" aria-label="Pipeline d’onboarding">
        <button className={`onb-etape${filtre === 'tous' ? ' on' : ''}`} role="tab" aria-selected={filtre === 'tous'} onClick={() => setFiltre('tous')}>
          <div className="n">{dossiers?.length ?? '–'}</div><div className="l">Tous les dossiers</div>
        </button>
        {STATUTS.map((s) => (
          <button
            key={s}
            className={`onb-etape${filtre === s ? ' on' : ''}${(s === 'refuse' || s === 'suspendu') && compte[s] > 0 ? ' alerte' : ''}`}
            role="tab" aria-selected={filtre === s} onClick={() => setFiltre(s)}
          >
            <div className="n">{dossiers ? compte[s] : '–'}</div><div className="l">{LIBELLE_STATUT[s]}</div>
          </button>
        ))}
      </div>

      <div className="card" data-anim>
        <div className="card-head">
          <div>
            <h3><Icone nom="shield" classe="h-ic" /> Onboarding PME</h3>
            <div className="ch-sub">{visibles.length} dossier{visibles.length > 1 ? 's' : ''}{filtre !== 'tous' ? ` · ${LIBELLE_STATUT[filtre]}` : ''} · une PME ne reçoit des courses qu’une fois active</div>
          </div>
          <span className="sp" />
          <input
            type="search" aria-label="Rechercher un dossier" placeholder="Rechercher…" value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
            style={{ width: 190, padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 10, marginRight: 8 }}
          />
          {proprietaire && <button className="btn btn-outline btn-sm" style={{ marginRight: 8 }} onClick={() => setModeles(true)}>Modèles de contrat</button>}
          {peutGerer && <button className="btn btn-gold btn-sm" onClick={() => setCreation(true)}><Icone nom="plus" /> Nouvelle PME</button>}
        </div>
        <div className="card-body flush">
          {dossiers === null ? (
            <p className="onb-liste-vide">Chargement…</p>
          ) : visibles.length === 0 ? (
            <p className="onb-liste-vide">
              {dossiers.length === 0 ? 'Aucune PME pour l’instant. « Nouvelle PME » ouvre un dossier au statut Prospect.' : 'Aucun dossier ne correspond à ce filtre.'}
            </p>
          ) : (
            <div className="tbl-wrap">
              <table>
                <thead>
                  <tr><th>Client</th><th>Statut juridique</th><th>Représentant</th><th>Téléphone</th><th>Forfait</th><th>Statut</th><th>Mis à jour</th></tr>
                </thead>
                <tbody>
                  {visibles.map((d) => (
                    <tr key={d.id} className="onb-ligne" tabIndex={0} onClick={() => setOuvertId(d.id)}
                      onKeyDown={(e) => { if (e.key === 'Enter') setOuvertId(d.id) }}>
                      <td>
                        <div className="t-strong">{d.name}</div>
                        {d.contact_name && <div className="t-muted">{d.contact_name}</div>}
                      </td>
                      <td>{statutDe(d) ? LIBELLE_COURT_STATUT[statutDe(d) as StatutJuridique] : <span className="t-muted">à qualifier</span>}</td>
                      <td>{nomSignataire(d) || <span className="t-muted">—</span>}</td>
                      <td className="mono">{d.phone ?? '—'}</td>
                      <td>{plans.find((p) => p.code === (offrePlan(d) ?? d.plan_code))?.name ?? <span className="t-muted">—</span>}</td>
                      <td><BadgeOnboarding statut={d.onboarding_status} /></td>
                      <td className="t-muted">{dateCourte(d.onboarding_updated_at ?? d.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {modeles && <ModelesContrat ouvert surFermer={() => setModeles(false)} />}
      {creation && (
        <ModaleClient
          type="pme"
          orgId={orgId}
          surFermer={() => setCreation(false)}
          surNotifier={surNotifier}
          surCree={async (message, id) => {
            setCreation(false)
            surNotifier(`${message} — dossier ouvert au statut Prospect`)
            await apresMaj()
            setOuvertId(id)
          }}
        />
      )}
    </>
  )
}

/** Forfait de l'offre choisie (jsonb), sinon null. */
function offrePlan(d: DossierPme): string | null {
  const o = d.offre_choisie as { plan_code?: unknown } | null
  return o && typeof o.plan_code === 'string' ? o.plan_code : null
}

const Erreur = ({ texte }: { texte: string | null }) => (texte ? <div className="bandeau erreur" role="alert">{texte}</div> : null)

